import React, { useCallback, useMemo } from "react"

import {
  DEFAULT_STATUS,
  getStatusLabel,
  normalizeRunStatus,
} from "@/runs/status"
import { Skeleton, Tooltip, type ColumnDef } from "@coval/ui"

import { BrowseTable as Table } from "@/components/tables/BrowseTable"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"
import { cn } from "@/utils/cn"

import type { ConversationsMetricTableRenderersById } from "../types/metricAdapters"
import {
  getPreferredConversationsColumnWidth,
  getResponsiveConversationsColumns,
  type VirtualConversationsTableColumn,
} from "../utils/getResponsiveConversationsColumns"
import { TableCellRenderer } from "./ConversationsTableCellRenderer"

/** Only this column flexes to fill leftover width; the rest stay fixed-width. */
const FLEXIBLE_COLUMN_KEYS = new Set(["customer_metadata"])

// The ID column renders a compact `CopyableId` (4 chars + copy icon), so it
// needs far less than its declared width — matches the runs table's ID column.
const ID_COLUMN_WIDTH = 56

// Metadata is the flexing column: it fills leftover width but must be able to
// shrink to this floor so it doesn't push the tags column off-screen (a flexing
// column's minWidth defaults to its width, which would pin it wide).
const METADATA_COLUMN_WIDTH = 280
const METADATA_COLUMN_MIN_WIDTH = 160

/** Stable row id shared by selection and active-row behavior. */
function uploadedConversationRowId(row: ConversationsTableResult): string {
  return row.simulation_output_id ?? `run-${row.run_id}`
}

/** Metric header: the (possibly truncated) label with the full label on hover. */
function ConversationsMetricHeader({
  label,
  fullLabel,
}: {
  label: string
  fullLabel?: string
}) {
  return (
    <Tooltip content={fullLabel || label}>
      <span className="block min-w-0 max-w-full truncate whitespace-nowrap">
        {label}
      </span>
    </Tooltip>
  )
}

interface BuildConversationsColumnsDeps {
  columns: VirtualConversationsTableColumn[]
  metricRenderersById: ConversationsMetricTableRenderersById
  isMetricsLoading: boolean
}

/**
 * Map the uploaded conversation column model onto `@coval/ui` `ColumnDef`s. Lives at module
 * scope (not inside the component) so the `cell`/`header` render callbacks
 * aren't treated as components defined during render.
 */
function buildConversationsColumns({
  columns,
  metricRenderersById,
  isMetricsLoading,
}: BuildConversationsColumnsDeps): ColumnDef<ConversationsTableResult>[] {
  return columns.map((column) => {
    const isMetric = column.key.startsWith("metric_")
    const isId = column.key === "simulation_output_id"
    const isTags = column.key === "tags"
    const isMetadata = column.key === "customer_metadata"
    return {
      id: column.key,
      // Metric headers can be long and get a tooltip with the full label; the
      // rest render their plain label.
      header: isMetric
        ? () => (
            <ConversationsMetricHeader
              label={column.label}
              fullLabel={column.fullLabel}
            />
          )
        : column.label,
      headerLabel: column.label,
      width: isId
        ? ID_COLUMN_WIDTH
        : isMetadata
          ? METADATA_COLUMN_WIDTH
          : getPreferredConversationsColumnWidth(column),
      // Let the flexing metadata column shrink below its width so it doesn't
      // shove the tags column off-screen (defaults to width otherwise).
      minWidth: isMetadata ? METADATA_COLUMN_MIN_WIDTH : undefined,
      // Metrics center under their header; tags right-align so their color-dot
      // chips + add button sit at the column's right edge (matches the runs
      // table). The ID column matches the runs table: a compact, left-aligned
      // `CopyableId` (rendered by the cell), right-pinned so the wide table
      // scrolls underneath it — no `variant: "id"` treatment.
      align: isMetric ? "center" : isTags ? "right" : undefined,
      pinned: isId ? "right" : undefined,
      // Only the wide metadata/tags columns absorb leftover width; fixed columns
      // (metrics, timestamps, id) never stretch.
      flex: FLEXIBLE_COLUMN_KEYS.has(column.key),
      cell: (row: ConversationsTableResult) => (
        <TableCellRenderer
          item={row}
          columnKey={column.key}
          metricRenderersById={metricRenderersById}
          isMetricsLoading={isMetricsLoading}
        />
      ),
    }
  })
}

interface VirtualConversationsTableProps {
  displayControls?: boolean
  rows: ConversationsTableResult[]
  columns: VirtualConversationsTableColumn[]
  visibleColumns: Set<string>
  columnControls?: React.ReactNode
  csvControl?: React.ReactNode
  metricRenderersById: ConversationsMetricTableRenderersById
  isMetricsLoading: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  isRefetching?: boolean
  activeRowId?: string | null
  selectedKeys?: Set<string>
  onSelectionChange?: (keys: Set<string>) => void
  /** Disables a selectable row's checkbox, e.g. once a picker hits its cap. */
  isRowSelectionDisabled?: (row: ConversationsTableResult) => boolean
  /**
   * Picker mode: every checkbox stays visible, select-all and shift-click
   * ranges are off, and a row click is the selection action (the `onRowClick`
   * handler toggles it).
   */
  pickerSelection?: boolean
  onRowClick: (simulationOutputId: string) => void
  /** Runs after the table opens a conversation in a new tab. */
  onRowOpenInNewTab?: (simulationOutputId: string) => void
  /** The conversation's own page, for new-tab opens and Copy URL. */
  conversationHref?: (simulationOutputId: string) => string
  onFetchNextPage: () => void
  onScrollPositionChange?: (scrollOffset: number) => void
  initialScrollOffset?: number
  scrollRef?: React.Ref<HTMLDivElement | null>
  onRowPointerEnter?: (rowId: string | null) => void
  onRowPointerLeave?: (rowId: string | null) => void
}

/**
 * Uploaded conversation data table, rendered on the shared `@coval/ui` `Table`.
 * The Table owns virtualization, the sticky header, infinite scroll, selection,
 * active-row scroll-into-view, and scroll restoration; this component maps the
 * uploaded conversation column model onto `ColumnDef`s and delegates the rest.
 */
export function VirtualConversationsTable({
  displayControls,
  rows,
  columns,
  visibleColumns,
  csvControl,
  columnControls,
  metricRenderersById,
  isMetricsLoading,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  isRefetching,
  activeRowId,
  selectedKeys = new Set<string>(),
  onSelectionChange,
  isRowSelectionDisabled,
  pickerSelection = false,
  onRowClick,
  onRowOpenInNewTab,
  conversationHref,
  onFetchNextPage,
  onScrollPositionChange,
  initialScrollOffset,
  scrollRef,
  onRowPointerEnter,
  onRowPointerLeave,
}: VirtualConversationsTableProps) {
  const visibleColumnsList = useMemo(
    () => getResponsiveConversationsColumns({ columns, visibleColumns }),
    [columns, visibleColumns],
  )

  const tableColumns = useMemo<ColumnDef<ConversationsTableResult>[]>(
    () =>
      buildConversationsColumns({
        columns: visibleColumnsList,
        metricRenderersById,
        isMetricsLoading,
      }),
    [visibleColumnsList, metricRenderersById, isMetricsLoading],
  )

  // A row click navigates to the result, except in picker mode where it toggles
  // the row; otherwise selection is handled by the hover-revealed checkbox
  // column, which never reaches this handler. A row without a simulation output
  // has no result to open.
  const handleTableRowClick = useCallback(
    (row: ConversationsTableResult) => {
      if (row.simulation_output_id) {
        onRowClick(row.simulation_output_id)
      }
    },
    [onRowClick],
  )
  const handleTableRowOpenInNewTab = useCallback(
    (row: ConversationsTableResult) => {
      if (row.simulation_output_id) {
        onRowOpenInNewTab?.(row.simulation_output_id)
      }
    },
    [onRowOpenInNewTab],
  )

  // Selection is always available (checkboxes reveal on row hover / focus); rows
  // without a simulation output id can't be selected.
  const selectionProps = onSelectionChange
    ? ({
        selectable: true,
        selectedIds: selectedKeys,
        onSelectionChange,
        isRowSelectable: (row: ConversationsTableResult) =>
          row.simulation_output_id !== null,
        isRowSelectionDisabled,
        showSelectAll: !pickerSelection,
        allowRangeSelection: !pickerSelection,
        selectionVisibility: pickerSelection ? "always" : "hover",
        rowClickSelects: pickerSelection,
      } as const)
    : ({ selectable: false } as const)

  return (
    <div
      data-testid="uploaded-conversations-table"
      className={cn(
        "relative h-full min-w-0",
        isRefetching && "pointer-events-none opacity-50 transition-opacity",
      )}
    >
      {/* Refetch spinner — overlaid top-right so it doesn't shift the header. */}
      {isRefetching && (
        <div className="pointer-events-none absolute right-3 top-2 z-20">
          <Skeleton className="h-4 w-4 rounded-full" />
        </div>
      )}
      <Table
        displayControls={displayControls}
        columnControls={columnControls}
        groupOptions={[
          {
            id: "status",
            label: "Status",
            value: (row) =>
              normalizeRunStatus(
                row.error_status || row.run_status || DEFAULT_STATUS,
              ),
            format: getStatusLabel,
          },
        ]}
        aria-label="Uploaded conversations table"
        data={rows}
        columns={tableColumns}
        getRowId={uploadedConversationRowId}
        csvControl={csvControl}
        rowCopyId
        rowHref={
          conversationHref
            ? (row) =>
                row.simulation_output_id
                  ? conversationHref(row.simulation_output_id)
                  : undefined
            : undefined
        }
        onRowOpenInNewTab={handleTableRowOpenInNewTab}
        // Highlight/view-context row anchor: lets Sofia glow a conversation row and lets
        // the view context report which conversations are actually on screen
        // (with their status). Rows without a simulation output id have no
        // stable entity to anchor. See app-covi/utils/viewContext.
        rowAttributes={(row) =>
          row.simulation_output_id
            ? {
                "data-covi-highlight-id": row.simulation_output_id,
                "data-covi-status": row.error_status || row.run_status,
              }
            : {}
        }
        onRowClick={handleTableRowClick}
        isRowClickable={(row) => row.simulation_output_id !== null}
        activeRowId={activeRowId}
        onRowPointerEnter={onRowPointerEnter}
        onRowPointerLeave={onRowPointerLeave}
        hasMore={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        isLoading={isLoading}
        onLoadMore={onFetchNextPage}
        initialScrollOffset={initialScrollOffset}
        onScrollChange={onScrollPositionChange}
        scrollRef={scrollRef}
        emptyState="No uploaded conversations found"
        className="h-full"
        {...selectionProps}
      />
    </div>
  )
}
